/**
 * Device protection level.
 *
 * The app this replaces ran protection off a setInterval that mutated server
 * state every tick. That produced, in order: charging that stopped whenever you
 * left the page, HTTP 429 storms, stale-closure bugs, and a dozen rounds of
 * useCallback dependency chasing.
 *
 * Here protection is a PURE FUNCTION of (settled level, elapsed time, context).
 * Nothing ticks. The database stores only `protection_level` +
 * `protection_updated_at`; anyone can derive the true current value at any
 * moment. Consequences:
 *
 *   - Correct after a reload, on another device, or after a week offline.
 *   - The UI can animate at 60fps by calling this with a fresh `now` and makes
 *     zero network requests to do it.
 *   - A write is only needed when CONTEXT changes (check in/out, subscription
 *     change), not on a timer.
 */

/** Baseline held by an active device on an active subscription. */
export const SUBSCRIBED_FLOOR = 75;
export const MAX_LEVEL = 100;
/** Ceiling for a device with no active subscription. */
export const UNSUBSCRIBED_CEILING = 9;
/** Ceiling for a deactivated device sitting inside a field. */
export const INACTIVE_CEILING = 75;

/** Decay is uniform: 100% -> 0% over 24h. Gives 100->75 in 6h, 9->0 in ~2h10m. */
export const DECAY_PER_MIN = 100 / (24 * 60);

export const RATE = {
  /** Subscribed + active device, inside a field: 1% per minute. */
  inFieldSubscribed: 1,
  /** No active subscription, inside a public field: 1% per 2 minutes. */
  inFieldUnsubscribed: 0.5,
  /** Deactivated device inside a field: 0 -> 75% over 3 days. */
  inFieldInactive: INACTIVE_CEILING / (3 * 24 * 60),
  /** Subscription baseline ramp: 0 -> 75% in 3 minutes. */
  baselineRamp: SUBSCRIBED_FLOOR / 3,
};

/**
 * Where protection is heading in this context.
 * @param {{hasSubscription:boolean, deviceActive:boolean, inField:boolean}} ctx
 */
export function protectionTarget({ hasSubscription, deviceActive, inField }) {
  if (!deviceActive) return inField ? INACTIVE_CEILING : 0;
  if (!hasSubscription) return inField ? UNSUBSCRIBED_CEILING : 0;
  return inField ? MAX_LEVEL : SUBSCRIBED_FLOOR;
}

function chargeRate({ hasSubscription, deviceActive, inField }) {
  if (!deviceActive) return RATE.inFieldInactive;
  if (!hasSubscription) return RATE.inFieldUnsubscribed;
  // Below the floor and out of a field means the subscription baseline is still
  // ramping up; inside a field it is the normal 1%/min charge.
  return inField ? RATE.inFieldSubscribed : RATE.baselineRamp;
}

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/**
 * Settle a stored protection level forward to `now`.
 *
 * @param {object}  args
 * @param {number}  args.level      last settled level (0-100)
 * @param {Date|string|number} args.updatedAt when that level was settled
 * @param {Date|number} [args.now]
 * @param {boolean} args.hasSubscription
 * @param {boolean} args.deviceActive
 * @param {boolean} args.inField
 * @returns {{level:number, target:number, isCharging:boolean, ratePerMin:number,
 *            minutesToTarget:number|null, elapsedMin:number}}
 */
export function settleProtection({
  level, updatedAt, now = Date.now(), hasSubscription, deviceActive, inField,
}) {
  const ctx = { hasSubscription, deviceActive, inField };
  const start = clamp(Number(level) || 0, 0, MAX_LEVEL);
  const elapsedMin = Math.max(0, (Number(new Date(now)) - Number(new Date(updatedAt))) / 60000);

  const target = protectionTarget(ctx);
  const isCharging = start < target;
  const ratePerMin = isCharging ? chargeRate(ctx) : DECAY_PER_MIN;

  const delta = ratePerMin * elapsedMin;
  const settled = isCharging
    ? Math.min(target, start + delta)
    : Math.max(target, start - delta);

  const remaining = Math.abs(target - settled);
  return {
    level: clamp(settled, 0, MAX_LEVEL),
    target,
    isCharging: isCharging && settled < target,
    ratePerMin,
    minutesToTarget: remaining > 0 && ratePerMin > 0 ? remaining / ratePerMin : null,
    elapsedMin,
  };
}

/** Convenience for rendering a device row straight from a DB record. */
export function deviceProtection(device, { hasSubscription, inField, now } = {}) {
  return settleProtection({
    level: device.protection_level,
    updatedAt: device.protection_updated_at,
    now,
    hasSubscription: Boolean(hasSubscription),
    deviceActive: device.is_active !== false,
    inField: inField ?? Boolean(device.checked_in_field_id),
  });
}
